import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getProductById } from '@/data/products';

const CartContext = createContext(null);

// Get cart from local storage
const getStoredCart = () => {
  try {
    const stored = localStorage.getItem('nexa_cart');
    return stored ? JSON.parse(stored) : { items: [] };
  } catch {
    return { items: [] };
  }
};

// Save cart to local storage
const saveCart = (cart) => {
  localStorage.setItem('nexa_cart', JSON.stringify(cart));
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const storedCart = getStoredCart();
    // Populate product details from static data
    const populatedItems = storedCart.items.map(item => ({
      ...item,
      product: getProductById(item.product_id)
    })).filter(item => item.product); // Remove items where product no longer exists
    setCart({ items: populatedItems });
  }, []);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    setLoading(true);
    
    // Simulate network delay for UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const product = getProductById(productId);
    if (!product) {
      setLoading(false);
      return false;
    }

    setCart(prevCart => {
      const existingItem = prevCart.items.find(i => i.product_id === productId);
      let newItems;
      
      if (existingItem) {
        newItems = prevCart.items.map(item =>
          item.product_id === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...prevCart.items, { product_id: productId, quantity, product }];
      }
      
      const newCart = { items: newItems };
      saveCart({ items: newItems.map(({ product_id, quantity }) => ({ product_id, quantity })) });
      return newCart;
    });
    
    setLoading(false);
    return true;
  }, []);

  const updateQuantity = useCallback(async (productId, quantity) => {
    setLoading(true);
    
    setCart(prevCart => {
      const newItems = prevCart.items.map(item =>
        item.product_id === productId
          ? { ...item, quantity }
          : item
      );
      
      saveCart({ items: newItems.map(({ product_id, quantity }) => ({ product_id, quantity })) });
      return { items: newItems };
    });
    
    setLoading(false);
  }, []);

  const removeFromCart = useCallback(async (productId) => {
    setLoading(true);
    
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => item.product_id !== productId);
      saveCart({ items: newItems.map(({ product_id, quantity }) => ({ product_id, quantity })) });
      return { items: newItems };
    });
    
    setLoading(false);
  }, []);

  const clearCart = useCallback(async () => {
    setLoading(true);
    setCart({ items: [] });
    saveCart({ items: [] });
    setLoading(false);
  }, []);

  const cartItemCount = cart.items?.reduce((total, item) => total + item.quantity, 0) || 0;
  
  const cartSubtotal = cart.items?.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + (price * item.quantity);
  }, 0) || 0;

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      cartItemCount,
      cartSubtotal
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
